'use client';

import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import clsx from 'clsx';
import { format, differenceInDays, addDays } from 'date-fns';
import { isFilled } from '@prismicio/helpers';
import { RichText } from '@/app/lib/RichText';
import { Settings } from '@/app/lib/settings';
import { NewsletterSignup } from '@/app/lib/NewsletterSignup';
import styles from './newsletterPopup.module.scss';

export function NewsletterPopup({ newsletterPopupData }) {
  const [showPopup, setShowPopup] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('initial'); 

  useEffect(() => {
    
    const cookieValues = Cookies.get(Settings.cookieName);
    const parsedCookie = cookieValues ? JSON.parse(cookieValues) : {};

    if (!parsedCookie?.newsletterPopupClosed || differenceInDays(new Date(), new Date(parsedCookie?.newsletterPopupClosed?.split('-').reverse().join('-'))) > (newsletterPopupData?.data?.cookie_expiry_days || 7)) {
      const timer = setTimeout(() => {
        setShowPopup(true);
        setTimeout(() => setIsTransitioning(true), 50); 
      }, newsletterPopupData?.data?.popup_delay_seconds * 1000);

      return () => clearTimeout(timer);
    }
  }, [newsletterPopupData?.data?.cookie_expiry_days, newsletterPopupData?.data?.popup_delay_seconds]);

  const setDismissCookie = (customDate = null) => {
    let cookieValues = Cookies.get(Settings.cookieName);
    let parsedCookie = cookieValues ? JSON.parse(cookieValues) : {};

    const dateToStore = customDate || new Date();
    
    const currentDate = format(dateToStore, 'dd-MM-yyyy');
    parsedCookie.newsletterPopupClosed = currentDate;

    Cookies.set(Settings.cookieName, JSON.stringify(parsedCookie), { 
      expires: Settings.cookieExpiry, 
      path: '/',
      sameSite: 'strict'
    });
  }

  const handleDismiss = () => {
    setDismissCookie(); 
    setIsTransitioning(false); 

    // Wait for the fade-out duration (500ms from CSS) before hiding the component
    setTimeout(() => {
      setShowPopup(false);
    }, 500); 
  };
  
  const handleSubmissionComplete = (status) => {
    setSubmissionStatus(status);
    
    if (status === 'success') {
      const futureDate = addDays(new Date(), 365);
      setDismissCookie(futureDate);
      setIsTransitioning(false);
      
      // Wait for the fade-out duration (500ms from CSS) before hiding the component
      setTimeout(() => {
        setShowPopup(false);
      }, 500); 
    }
  };

  if (!showPopup) {
    return null;
  }

  const innerClasses = clsx(
    styles.inner, 
    {
      [styles['is-visible']]: isTransitioning,
      [styles['is-hidden']]: !isTransitioning,
    }
  );

  return (
    <div className={clsx(styles.wrapper)}>
      <div className={innerClasses}>
        <div className={clsx(styles.close)} onClick={handleDismiss}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
            <line x1="0.53033" y1="0.530339" x2="19.6222" y2="19.6222" stroke="white" strokeWidth="1.5"/>
            <line x1="19.9795" y1="1.24017" x2="0.887664" y2="20.3321" stroke="white" strokeWidth="1.5"/>
          </svg>
        </div>
          
        <div className={clsx(styles.content)}>
          { submissionStatus === 'initial' && isFilled.richText(newsletterPopupData?.data?.initial_content) && (
            <RichText field={newsletterPopupData?.data?.initial_content} />
          )}
        </div>

        <div className={clsx(styles.content)}>
          { submissionStatus === 'success' && isFilled.richText(newsletterPopupData?.data?.successful_content) && (
            <RichText field={newsletterPopupData?.data?.successful_content} />
          )}
        </div>

        <div className={clsx(styles.content)}>
          { submissionStatus === 'error' && isFilled.richText(newsletterPopupData?.data?.unsuccessful_content) && (
            <RichText field={newsletterPopupData?.data?.unsuccessful_content} />
          )}
        </div>
        
        { submissionStatus === 'initial' && (
          <div className={clsx(styles.input)}>
            <NewsletterSignup 
              position={'popup'} 
              onSubmissionComplete={handleSubmissionComplete}
            />
          </div>
        )}
      </div>
    </div>
  );
};